<main class="canvas">
  @if (release(); as r) {
    <!-- Showstopper banner with View link (AC2) -->
    @if (showstopper(); as ss) {
      <div class="showstopper-banner">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2 1 21h22L12 2zm0 6 7.5 13h-15L12 8zm-1 3v5h2v-5h-2zm0 6v2h2v-2h-2z" />
        </svg>
        <span>
          <span class="strong"
            >{{ ss.count }} showstopper{{ ss.count === 1 ? '' : 's' }} flagged.</span
          >
          <span class="sub"> Release cannot ship until resolved.</span>
        </span>
        @if (showstopperPlan()) {
          <a class="view-link" href="javascript:void(0)" (click)="scrollToShowstopper()">View →</a>
        }
      </div>
    }

    <!-- Release summary with Manager view badge (AC1) -->
    <section class="release-summary">
      <div class="release-summary-top">
        <div>
          <div class="eyebrow">Active release <span class="role-badge">Manager view</span></div>
          <h1 class="release-name">{{ r.releaseName }}</h1>
        </div>
        <div class="summary-progress">
          <div class="overall-progress">
            <div class="overall-progress-bar">
              <div class="fill" [style.width.%]="stats().pct"></div>
              <div
                class="fail-fill"
                [style.left.%]="stats().pct"
                [style.width.%]="stats().failPct"
              ></div>
            </div>
            <div class="overall-progress-label">{{ stats().pct }}%</div>
          </div>
          <div class="complete-count">
            {{ stats().complete }} of {{ stats().total }} tests complete
          </div>
        </div>
      </div>

      <div class="release-stats">
        <div class="stat">
          <div class="stat-label">Passed</div>
          <div class="stat-value">
            <span class="pass-num">{{ stats().passed }}</span> tests
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">Failed</div>
          <div class="stat-value">
            <span class="fail-num">{{ stats().failed }}</span> tests
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">Remaining</div>
          <div class="stat-value">{{ stats().remaining }} tests</div>
        </div>
      </div>
    </section>

    <!-- Search on its own line (AC3) -->
    <div class="toolbar search-row">
      <div class="search-input">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="text"
          placeholder="Search test plans"
          [value]="search()"
          (input)="setSearch($any($event.target).value)"
          aria-label="Search test plans"
        />
        @if (search()) {
          <button
            type="button"
            class="search-clear"
            aria-label="Clear search"
            title="Clear search"
            (click)="setSearch('')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- View toggle: stays on both pages so the manager view is not a one-way
         door back to My view (Bug 56799). -->
    <div class="toolbar view-toggle-row">
      <div class="chip-group">
        <button type="button" class="chip" (click)="goToMyView()">
          My view <span class="count">{{ myViewCount() }}</span>
        </button>
        <button type="button" class="chip active" aria-current="page">
          Everyone <span class="count">{{ everyoneCount() }}</span>
        </button>
      </div>
    </div>

    <!-- Three dropdown filters + Export (AC4/AC5/AC6) -->
    <div class="filter-bar">
      <div class="filter-group">
        <label for="f-assignee">Assignee</label>
        <app-select
          ariaLabel="Assignee"
          [options]="assigneeOptions()"
          [ngModel]="assigneeFilter()"
          (ngModelChange)="setAssigneeFilter($event)"
        />
      </div>

      <div class="filter-group">
        <label for="f-status">Status</label>
        <app-select
          ariaLabel="Status"
          [options]="statusOptions()"
          [ngModel]="statusFilter()"
          (ngModelChange)="setStatusFilter($event)"
        />
      </div>

      <div class="filter-group">
        <label for="f-severity">Severity</label>
        <app-select
          ariaLabel="Severity"
          [options]="severityOptions()"
          [ngModel]="severityFilter()"
          (ngModelChange)="setSeverityFilter($event)"
        />
      </div>

      <button class="btn btn-ghost export-btn" type="button" (click)="exportCsv()">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="14"
          height="14"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export CSV
      </button>
    </div>

    <!-- Flat plan list (AC1) -->
    <div class="plan-list">
      @for (p of filteredPlans(); track trackByPlanId($index, p)) {
        <div
          class="plan-row"
          [class.has-showstopper]="p.numberOfShowstoppers > 0"
          [class.highlight]="highlightedPlanId() === p.testPlanId"
          [id]="'plan-' + p.testPlanId"
        >
          <div class="plan-status {{ statusClass(p) }}" [title]="statusSub(p)">
            @switch (p.status) {
              @case ('Completed') {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              }
              @case ('Failing') {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                >
                  <path
                    d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                  />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              }
              @case ('InProgress') {
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              }
              @default {
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="12" r="9" />
                </svg>
              }
            }
          </div>

          <div class="plan-info">
            <div class="plan-name">
              {{ p.testPlanName }}
              @if (p.numberOfShowstoppers > 0) {
                <span class="ss-pill">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="10"
                    height="10"
                    aria-hidden="true"
                  >
                    <path d="M12 2 1 21h22L12 2zm-1 9v5h2v-5h-2zm0 6v2h2v-2h-2z" />
                  </svg>
                  Showstopper
                </span>
              }
            </div>
            <div class="plan-sub">{{ statusSub(p) }}</div>
          </div>

          <div class="plan-progress">
            <div class="mini-bar">
              <div class="fill" [style.width.%]="progressPct(p)"></div>
              @if (p.numberOfFailingTests > 0) {
                <div
                  class="fail-fill"
                  [style.left.%]="progressPct(p)"
                  [style.width.%]="failPct(p)"
                ></div>
              }
            </div>
            <div class="mini-count">{{ p.numberOfPassingTests }} / {{ p.numberOfTests }}</div>
          </div>

          <!-- Assignee with clickable avatar + full name (AC7/AC8) -->
          <div class="plan-assignee">
            <button
              class="assignee-avatar"
              type="button"
              [class.mine]="isMine(p)"
              [class.unassigned]="p.assignedUserId == null"
              [title]="p.assignedUserName ?? 'Unassigned. Click to assign.'"
              (click)="popoverSearch = ''"
              [cdkMenuTriggerFor]="assignMenu"
            >
              {{ p.assignedUserId == null ? '+' : initials(p.assignedUserName) }}
            </button>
            <span class="assignee-name" [class.mine]="isMine(p)">
              {{ p.assignedUserName ?? 'Unassigned' }}
            </span>

            <ng-template #assignMenu>
              <div class="popover assign-popover" cdkMenu>
                <div class="popover-header">Assign to</div>
                <input
                  class="popover-search"
                  type="text"
                  placeholder="Search people…"
                  [(ngModel)]="popoverSearch"
                  (keydown)="$event.stopPropagation()"
                />
                @for (u of popoverPeople(); track trackByUserId($index, u)) {
                  <button
                    class="popover-row"
                    cdkMenuItem
                    [class.active]="p.assignedUserId === u.userId"
                    (click)="reassign(p, u)"
                  >
                    <span class="mini-avatar" [class.mine]="u.userId === currentUserId()">{{
                      initials(u.firstName + ' ' + u.lastName)
                    }}</span>
                    <span
                      >{{ u.firstName }} {{ u.lastName
                      }}{{ u.userId === currentUserId() ? ' (me)' : ''
                      }}{{ p.assignedUserId === u.userId ? ' · current' : '' }}</span
                    >
                  </button>
                }
                <div class="popover-divider"></div>
                <button class="popover-row danger" cdkMenuItem (click)="unassign(p)">
                  <span class="mini-avatar empty">−</span>
                  <span>Unassign</span>
                </button>
              </div>
            </ng-template>
          </div>

          <button class="plan-cta" type="button" [disabled]="isOpening(p)" (click)="openPlan(p)">
            {{ isOpening(p) ? 'Opening…' : cta(p) }}
            @if (isOpening(p)) {
              <span class="spinner spinner-sm" aria-hidden="true"></span>
            } @else {
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                width="14"
                height="14"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            }
          </button>
        </div>
      } @empty {
        <div class="plan-empty">No plans match your filters.</div>
      }
    </div>
  } @else {
    <div class="plan-empty">Loading release…</div>
  }
</main>
